import {ActionIcon, InputLabel, Popover, Select} from '@mantine/core'
import dayjs, {type Dayjs} from 'dayjs'
import {IconChevronLeft, IconChevronRight, IconPointFilled} from '@tabler/icons-react'
import {formatLocalWeek} from '../../screens/calendar/formatter/formatLocalWeek.ts'
import {formatLocalMonth} from '../../screens/dashboard/formatter/formatLocalMonth.ts'
import {WeekSelector} from './components/WeekSelector.tsx'
import {MonthSelector} from './components/MonthSelector.tsx'
import {useUserSettings} from '../../providers/userSettings/useUserSettings.ts'

export type DateRange = {end: Dayjs; start: Dayjs}

type Props = {
  range: 'week' | 'month'
  value: DateRange
  onValueChange: (dateRange: DateRange) => void
  disabled: boolean
}

export const DateRangeSelector = ({range, value, onValueChange, disabled}: Props) => {
  const {userSettings} = useUserSettings()
  const maxDate = {week: undefined, month: dayjs().endOf('month')}[range]

  return (
    <div>
      <InputLabel pb={1}>{{week: 'Week', month: 'Month'}[range]}</InputLabel>

      <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 5}}>
        <Popover position='bottom' withArrow shadow={'md'}>
          <Popover.Target>
            <Select
              value={range}
              data={[
                {value: 'week', label: formatLocalWeek(value.start, userSettings)},
                {value: 'month', label: formatLocalMonth(value.start, userSettings)},
              ]}
              style={{minWidth: 170, width: '100%'}}
              title={{week: 'Select week', month: 'Select month'}[range]}
              dropdownOpened={false}
              disabled={disabled}
            />
          </Popover.Target>
          <Popover.Dropdown>
            {
              {
                week: (
                  <WeekSelector value={value} onValueChange={onValueChange} maxDate={maxDate} disabled={disabled} />
                ),
                month: (
                  <MonthSelector value={value} onValueChange={onValueChange} maxDate={maxDate} disabled={disabled} />
                ),
              }[range]
            }
          </Popover.Dropdown>
        </Popover>

        <ActionIcon.Group>
          <ActionIcon
            variant={'default'}
            h={36}
            title={{week: 'Previous week', month: 'Previous month'}[range]}
            onClick={() => onValueChange({start: value.start.subtract(1, range), end: value.end.subtract(1, range)})}
            disabled={disabled}
          >
            <IconChevronLeft stroke={1} />
          </ActionIcon>
          <ActionIcon
            variant={'default'}
            h={36}
            title={{week: 'This week', month: 'This month'}[range]}
            onClick={() => onValueChange({start: dayjs().startOf(range), end: dayjs().endOf(range)})}
            disabled={disabled}
          >
            <IconPointFilled stroke={1} size={16} />
          </ActionIcon>
          <ActionIcon
            variant={'default'}
            h={36}
            title={{week: 'Next week', month: 'Next month'}[range]}
            onClick={() => onValueChange({start: value.start.add(1, range), end: value.end.add(1, range)})}
            disabled={disabled || (maxDate && value.start.add(1, range).isAfter(maxDate))}
          >
            <IconChevronRight stroke={1} />
          </ActionIcon>
        </ActionIcon.Group>
      </div>
    </div>
  )
}
